import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/api';

interface FormState {
  rutEmpresa: string;
  razonSocial: string;
  password: string;
  confirmarPassword: string;
  direccion: string;
  telefono: string;
  personaContacto: string;
  telefonoContacto: string;
  correoContacto: string;
}

const Registro = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const token = searchParams.get('token') || '';

  const [tokenState, setTokenState] = useState<'loading' | 'valid' | 'invalid'>('loading');
  const [tokenError, setTokenError] = useState('');
  const [emailFromToken, setEmailFromToken] = useState('');

  const [form, setForm] = useState<FormState>({
    rutEmpresa: '',
    razonSocial: '',
    password: '',
    confirmarPassword: '',
    direccion: '',
    telefono: '',
    personaContacto: '',
    telefonoContacto: '',
    correoContacto: '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // ── Validar token al montar ───────────────────────────────────────────────
  useEffect(() => {
    if (!token) {
      setTokenState('invalid');
      setTokenError('No se encontró un enlace de invitación válido. Contacta a tu administrador.');
      return;
    }

    fetch(API_ENDPOINTS.validateInvitation(token))
      .then(res => res.json())
      .then(data => {
        if (data.valid && data.email) {
          setEmailFromToken(data.email);
          setTokenState('valid');
        } else {
          setTokenState('invalid');
          setTokenError(data.message || 'Este enlace ha expirado o ya fue utilizado. Contacta a tu administrador.');
        }
      })
      .catch(() => {
        setTokenState('invalid');
        setTokenError('No se pudo verificar el enlace. Intenta nuevamente o contacta a tu administrador.');
      });
  }, [token]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (form.password !== form.confirmarPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }
    if (form.password.length < 8) {
      setError('La contraseña debe tener al menos 8 caracteres');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(API_ENDPOINTS.completeRegistration, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          email: emailFromToken,
          ...form,
        }),
      });

      const data = await res.json();

      if (res.ok && data.success) {
        navigate('/login', {
          state: { message: 'Registro completado exitosamente. Ya puedes iniciar sesión.' }
        });
      } else {
        setError(data.message || 'Error al completar el registro');
      }
    } catch {
      setError('Error de conexión. Por favor intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const inputClass = 'w-full border border-gray-300 rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400';
  const labelClass = 'block text-sm font-medium text-gray-700 mb-1';

  // ── Estado: verificando token ─────────────────────────────────────────────
  if (tokenState === 'loading') {
    return (
      <div className="max-w-md mx-auto mt-16 px-4 text-center">
        <p className="text-gray-600">Verificando enlace de invitación...</p>
      </div>
    );
  }

  // ── Estado: token inválido ────────────────────────────────────────────────
  if (tokenState === 'invalid') {
    return (
      <div className="max-w-md mx-auto mt-16 px-4">
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <div className="text-red-500 text-4xl mb-4">✕</div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">Enlace no válido</h2>
          <p className="text-gray-600">{tokenError}</p>
        </div>
      </div>
    );
  }

  // ── Estado: token válido → mostrar formulario ─────────────────────────────
  return (
    <div className="max-w-2xl mx-auto mt-10 mb-12 px-4">
      <div className="bg-white rounded-lg shadow p-8">
        <h1 className="text-2xl font-bold mb-6 text-center">Completa tu Registro</h1>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        <form className="space-y-4" onSubmit={handleSubmit}>

          {/* Correo — bloqueado, viene del token */}
          <div>
            <label className={labelClass}>Correo Electrónico</label>
            <input
              type="email"
              value={emailFromToken}
              readOnly
              className="w-full border border-gray-300 rounded px-4 py-2 bg-gray-100 text-gray-500 cursor-not-allowed"
            />
            <p className="text-xs text-gray-500 mt-1">
              Este correo está asociado a tu invitación y no puede modificarse.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>RUT Empresa *</label>
              <input
                name="rutEmpresa"
                value={form.rutEmpresa}
                onChange={handleChange}
                required
                placeholder="12.345.678-9"
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Razón Social *</label>
              <input
                name="razonSocial"
                value={form.razonSocial}
                onChange={handleChange}
                required
                className={inputClass}
              />
            </div>
          </div>

          <div>
            <label className={labelClass}>Dirección *</label>
            <input
              name="direccion"
              value={form.direccion}
              onChange={handleChange}
              required
              className={inputClass}
            />
          </div>

          <div>
            <label className={labelClass}>Teléfono *</label>
            <input
              name="telefono"
              value={form.telefono}
              onChange={handleChange}
              required
              className={inputClass}
            />
          </div>

          <hr className="my-2" />
          <p className="text-sm font-semibold text-gray-600">Persona de Contacto</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Nombre *</label>
              <input
                name="personaContacto"
                value={form.personaContacto}
                onChange={handleChange}
                required
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Teléfono de Contacto *</label>
              <input
                name="telefonoContacto"
                value={form.telefonoContacto}
                onChange={handleChange}
                required
                className={inputClass}
              />
            </div>
          </div>

          <div>
            <label className={labelClass}>Correo de Contacto *</label>
            <input
              name="correoContacto"
              type="email"
              value={form.correoContacto}
              onChange={handleChange}
              required
              className={inputClass}
            />
          </div>

          <hr className="my-2" />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Contraseña *</label>
              <input
                name="password"
                type="password"
                value={form.password}
                onChange={handleChange}
                required
                minLength={8}
                placeholder="Mínimo 8 caracteres"
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Confirmar Contraseña *</label>
              <input
                name="confirmarPassword"
                type="password"
                value={form.confirmarPassword}
                onChange={handleChange}
                required
                className={inputClass}
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-500 hover:bg-blue-400 text-white font-semibold py-3 px-8 rounded transition mt-2 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {loading ? 'Registrando...' : 'Completar Registro'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Registro;
